import ComparisonType from "src/types/ComparisonType";
import { type CountValue } from "src/types/countItemDto";
import { type MeasuresItemizedDto, type MeasuresItemizedItemDto } from "src/types/locationChartDto";
import type LocationType from "src/types/LocationType";

import { NUMBER_LOCALE } from "./config.helper";
import {
  type MeasuresSummarizedItemDto,
  type MeasureItemDto,
  type MeasuresSummarizedDto,
  type MeasuresItemizedCompactDto,
  type MeasureItem,
  type MeasuresItemizedCompactItemDto,
} from "../types/dashboardDatasetDto";
import { type DateRange } from "../types/dateRange";
import { type FlowMapFeatureCollection } from "../types/FlowMapFeatureCollection";

export function toShortNumberFormat(number: number): string {
  const sign = Math.sign(number);
  let unit = 0;

  let shortenedNumber = number;
  while (Math.abs(shortenedNumber) >= 1000) {
    unit += 1;
    shortenedNumber = Math.floor(Math.abs(shortenedNumber) / 100) / 10;
  }

  let shortenedNumberUnit;
  switch (unit) {
    case 1: {
      shortenedNumberUnit = "K";
      break;
    }
    case 2: {
      shortenedNumberUnit = "M";
      break;
    }
    case 3: {
      shortenedNumberUnit = "G";
      break;
    }
    default: {
      shortenedNumberUnit = "";
    }
  }

  return `${sign * Math.abs(shortenedNumber)}${shortenedNumberUnit}`;
}

export function formatNum(num: number): string {
  if (NUMBER_LOCALE === "de-CH") {
    return num.toLocaleString(NUMBER_LOCALE).replace(/’/g, "'");
  } 
  return num.toLocaleString(NUMBER_LOCALE);
}

export function getPeriodName(dateRange: DateRange): number {
  return new Date(dateRange.start).getFullYear();
}

export function getMaxCount(data: MeasuresSummarizedDto): number {
  return Math.max(...data.data.map((item) => item.currentValue ?? 0));
}

export function getFlowMapMaxCount(collection: FlowMapFeatureCollection): number {
  return Math.max(...collection.features.map((item) => item.properties?.frequency ?? 0));
}

export function getExpectedVisitors(pastPeriodSum: number, currentPeriodSum: number, pastPeriodWholeDaySum: number): number {
  if (pastPeriodSum === 0 || Number.isNaN(currentPeriodSum) || Number.isNaN(pastPeriodSum)) {
    return pastPeriodWholeDaySum;
  }

  const diff = currentPeriodSum / pastPeriodSum - 1;

  return Number.isNaN(diff) ? pastPeriodWholeDaySum : (1 + diff) * pastPeriodWholeDaySum;
}

/**
 * returns the count of all items before the current time int
 * @param levelItems the level items map with the count date values
 * @param timeInt the current time int
 * @returns
 */
export function getCountDateTotalPriorCount(levelItems: Map<number, MeasureItemDto[]>, timeInt: number): number {
  return getCountDateTotalCount(
    [...levelItems.entries()].filter(([otherTimeInt]) => otherTimeInt <= timeInt).flatMap(([, measureItemDtos]) => measureItemDtos)
  );
}

/**
 * returns the total of all counts combined of an MeasureItemDto array
 * @param measureItemDtos the MeasureItemDto array
 * @returns total
 */
export function getCountDateTotalCount(measureItemDtos: MeasureItemDto[]): number {
  return measureItemDtos.reduce((acc, curr) => acc + (curr.count ?? 0), 0);
}

/**
 * returns the total of all past counts combined of an MeasureItemDto array
 * @param measureItemDtos the MeasureItemDto array
 * @returns total
 */
export function getCountDateTotalPastCount(measureItemDtos: MeasureItem[]): number {
  return measureItemDtos.reduce((acc, curr) => acc + (curr.countPast ?? 0), 0);
}
/**
 * returns the total of all current values combined of an MeasuresSummarizedItemDto Array
 * @param items the MeasuresSummarizedItemDto Array
 * @returns total of all current values
 */
export function getCurrentTotalCount(items: Array<MeasuresItemizedItemDto | MeasuresSummarizedItemDto>): number {
  return items.reduce((acc, curr) => acc + (curr.currentValue ?? 0), 0);
}

/**
 * returns the total of all past values combined of an MeasuresSummarizedItemDto Array
 * @param items the MeasuresSummarizedItemDto Array
 * @returns total of all past values
 */
export function getPastTotalCount(items: Array<MeasuresItemizedItemDto | MeasuresSummarizedItemDto>): number {
  return items.reduce((acc, curr) => acc + (curr.pastValue ?? 0), 0);
}

/**
 * Get Sum of In and Out Count by MeasureTime
 * @param chartData the chart data
 * @returns Record<string, number[]>
 */
export function getSumByTime(chartData: CountValue[]): Record<string, number[]> {
  const tmpSumByTime: Record<string, number[]> = {};
  for (const countValue of chartData) {
    const inCount = countValue.InCount ?? 0;
    const outCount = countValue.OutCount ?? 0;
    const time = countValue?.MeasureTime.valueOf();
    if (tmpSumByTime[time]) {
      tmpSumByTime[time][0] += inCount;
      tmpSumByTime[time][1] += outCount;
    } else {
      tmpSumByTime[time] = [inCount, outCount];
    }
  }
  return tmpSumByTime;
}

/**
 * returns the difference between the current and past values in percent
 * @param measuresData the measures data as MeasuresItemizedDto or MeasuresSummarizedDto
 * @returns difference in percent or null when uncomparable
 */
export function getDifference(measuresData: MeasuresItemizedDto | MeasuresSummarizedDto | undefined | null): number | null {
  if (!measuresData?.data) return null;
  const [currentSum, pastSum] = getTotalCurrentAndPastValues(measuresData.data);
  if (currentSum === 0 || pastSum === 0) return null;
  return ((currentSum - pastSum) / pastSum) * 100;
}

export function getDifferenceForRow(row: MeasuresSummarizedItemDto): number | null {
  if (row.currentValue === 0 || row.pastValue === 0 || !row.currentValue || !row.pastValue) return null;
  return ((row.currentValue - row.pastValue) / row.pastValue) * 100;
}

export function getDifferenceForIntervalOverviewTabs(measuresData: MeasuresItemizedCompactDto | undefined | null): number | null {
  if (!measuresData?.data) return null;
  const currentSum = getCountDateTotalCount(measuresData.data.flatMap((item) => item.currentValues ?? []));
  const pastSum = getCountDateTotalCount(measuresData.data.flatMap((item) => item.pastValues ?? []));
  if (currentSum === 0 || pastSum === 0) return null;
  return ((currentSum - pastSum) / pastSum) * 100;
}

/**
 * get the total in and out values of a CountValue array
 * @param chartData the chart data as CountValue array
 * @returns the total of in and out values
 */
export function getTotalInOutValues(chartData: CountValue[]): number[] {
  let inSum = 0;
  let outSum = 0;
  for (const item of chartData) {
    inSum += item.InCount ?? 0;
    outSum += item.OutCount ?? 0;
  }
  return [inSum, outSum];
}

/**
 * Get the total of current and past values of a MeasuresItemizedItemDto array
 * @param chartData the chart data as MeasuresItemizedItemDto array
 * @returns the total of current and past values
 */
export function getTotalCurrentAndPastValues(chartData: Array<MeasuresItemizedItemDto | MeasuresSummarizedItemDto>): number[] {
  let currentSum = 0;
  let pastSum = 0;
  for (const item of chartData) {
    currentSum += item.currentValue ?? 0;
    pastSum += item.pastValue ?? 0;
  }
  return [currentSum, pastSum];
}

/**
 * Calculates the median value of an array of numbers.
 * @param array - The array of numbers.
 * @returns The median of the numbers in the array.
 */
export function getMedian(array: number[]): number {
  array.sort((a, b) => a - b);
  const middleIndex = Math.floor(array.length / 2);

  if (array.length % 2 === 0) {
    return (array[middleIndex - 1] + array[middleIndex]) / 2;
  }
  return array[middleIndex];
}

/**
 * Calculates the average of an array of numbers.
 * @param array - The array of numbers.
 * @returns The average of the numbers in the array.
 */
export function getAverage(array: number[]): number {
  if (array.length === 0) {
    return Number.NaN;
  }
  return array.reduce((a, b) => a + b) / array.length;
}

export function getValue(item: MeasuresSummarizedItemDto, comparisonValue: ComparisonType): number {
  if (comparisonValue === ComparisonType.Absolute) {
    return item.currentValue ?? 0;
  }
  if (item.pastValue) {
    return (item.changeValue ?? 0) / item.pastValue;
  }
  return 0;
}

export function getAvgTemperature(items: MeasuresItemizedItemDto[]): number {
  const temperatures = items.map((item) => Number.parseFloat(item.temperature)).filter((temp) => !Number.isNaN(temp));

  if (temperatures.length === 0) {
    return 0;
  }

  const sum = temperatures.reduce((acc, temp) => acc + temp, 0);
  return Math.round(sum / temperatures.length);
}

type SortFn<T> = (a: T, b: T) => number;
type FilterFn<T> = (item: T) => boolean;

// sort function for two MeasuresSummarizedItemDto elements by Current Value descending
const sortByCurrentValueDesc: SortFn<MeasuresSummarizedItemDto> = (a, b): number => (b.currentValue ?? 0) - (a.currentValue ?? 0);
const filterPassAll: FilterFn<MeasuresSummarizedItemDto | MeasuresItemizedCompactItemDto> = () => true;

export function filterAndSortMeasuresSummarizedDto(
  measuresData: MeasuresSummarizedDto | undefined | null,
  filterFn: FilterFn<MeasuresSummarizedItemDto> = filterPassAll,
  sortFn: SortFn<MeasuresSummarizedItemDto> = sortByCurrentValueDesc,
  sliceStart: number | undefined = undefined,
  sliceEnd: number | undefined = undefined
): MeasuresSummarizedDto | undefined {
  if (!measuresData) return undefined;
  return {
    ...measuresData,
    data: measuresData?.data?.filter(filterFn).sort(sortFn).slice(sliceStart, sliceEnd),
  };
}

export function filterByLocationTypeAndSortMeasuresSummarizedDto(
  measuresData: MeasuresSummarizedDto | undefined | null,
  locationType: LocationType,
  sortFn: SortFn<MeasuresSummarizedItemDto> = sortByCurrentValueDesc,
  sliceStart: number | undefined = undefined,
  sliceEnd: number | undefined = undefined
): MeasuresSummarizedDto | undefined {
  return filterAndSortMeasuresSummarizedDto(measuresData, (item) => item.type === locationType, sortFn, sliceStart, sliceEnd);
}

function filterMeasuresItemizedCompactDto(
  measuresData: MeasuresItemizedCompactDto | undefined | null,
  filterFn: FilterFn<MeasuresItemizedCompactItemDto> = filterPassAll,
  sliceStart: number | undefined = undefined,
  sliceEnd: number | undefined = undefined
): MeasuresItemizedCompactDto | undefined {
  if (!measuresData) return undefined;
  return {
    ...measuresData,
    data: measuresData?.data?.filter(filterFn).slice(sliceStart, sliceEnd),
  };
}

export function filterByLocationTypeAndSortMeasuresItemizedCompactDto(
  measuresData: MeasuresItemizedCompactDto | undefined | null,
  locationType: LocationType,
  sliceStart: number | undefined = undefined,
  sliceEnd: number | undefined = undefined
): MeasuresItemizedCompactDto | undefined {
  return filterMeasuresItemizedCompactDto(measuresData, (item) => item.type === locationType, sliceStart, sliceEnd);
}

function compareMeasures(
  a: MeasuresSummarizedItemDto,
  b: MeasuresSummarizedItemDto,
  comparisonValue: ComparisonType,
  order: "asc" | "desc"
): number {
  if (comparisonValue === ComparisonType.GrowthRate) {
    const aHasPast = a.pastValue != null;
    const bHasPast = b.pastValue != null;

    if (!aHasPast && !bHasPast) return 0;
    if (!aHasPast) return -1;
    if (!bHasPast) return 1;
  }

  const diff = getValue(b, comparisonValue) - getValue(a, comparisonValue);
  return order === "asc" ? -diff : diff;
}

export function sortMeasuresData(
  data: MeasuresSummarizedItemDto[],
  comparisonValue: ComparisonType,
  order: "asc" | "desc"
): MeasuresSummarizedItemDto[] {
  return data.sort((a, b) => compareMeasures(a, b, comparisonValue, order));
}

export function createLabelFormatter(sortedChartData: MeasuresSummarizedItemDto[]) {
  return function (params: { name: string }): string {
    const item = sortedChartData.find((e) => e.localName === params.name);
    if (item == null) {
      return ``;
    }

    const currentValue = item.currentValue ?? 0;
    const pastValue = item.pastValue ?? 0;
    const value = `{black|${currentValue}}`;

    if (pastValue === 0 || currentValue === 0) {
      return `${value}      `;
    }

    const diffPercent = ((currentValue - pastValue) / pastValue) * 100;
    const diff =
      diffPercent >= 0 ? `{differencePositive|+${diffPercent.toFixed(1)}% }` : `{differenceNegative| ${diffPercent.toFixed(1)}% }`;

    return `${value} ${diff}     `;
  };
}
